/**
 * Reversal confirmation.
 *
 * This one DOES confirm, unlike ordinary saves which use undo instead: a
 * reversal is itself the undo, it is not undoable in turn, and it is rare
 * enough that the extra step costs nothing. The dialog spells out that the
 * original stays visible, because "сторнирай" reads like "delete" otherwise.
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/input';
import { Label } from '@/components/ui/misc';
import { ResponsiveDialog } from '@/components/ui/dialog';
import { Money, KIND_LABELS, toneForKind } from '@/components/Money';
import { formatDate } from '@/lib/format';
import { useVoidTransaction } from '@/hooks/useApi';
import type { Transaction } from '@/lib/types';

export function VoidDialog({
  tx,
  onClose,
}: {
  tx: Transaction | null;
  onClose: () => void;
}): JSX.Element {
  const [reason, setReason] = useState('');
  const voidTx = useVoidTransaction();

  useEffect(() => {
    if (tx) setReason('');
  }, [tx]);

  const handleVoid = async () => {
    if (!tx) return;
    try {
      await voidTx.mutateAsync({ id: tx.id, reason: reason || undefined });
      toast.success('Операцията е сторнирана');
      onClose();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const leg = tx?.postings.find((p) => p.amountMinor < 0) ?? tx?.postings[0];

  return (
    <ResponsiveDialog
      open={tx != null}
      onOpenChange={(o) => !o && onClose()}
      title="Сторниране на операция"
      description="Операцията не се изтрива. Тя остава видима и маркирана, а огледален запис връща салдото."
      className="sm:max-w-md"
    >
      {tx ? (
        <div className="space-y-4">
          <div className="rounded-md border bg-muted/40 p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate font-medium">
                  {tx.description || tx.category?.name || KIND_LABELS[tx.kind]}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {formatDate(tx.date)} · {KIND_LABELS[tx.kind]}
                </p>
              </div>
              {leg ? (
                <Money
                  minor={leg.amountMinor}
                  currency={leg.currency}
                  tone={toneForKind(tx.kind)}
                  signed
                  className="shrink-0 font-medium"
                />
              ) : null}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="void-reason">Причина (по избор)</Label>
            <Textarea
              id="void-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="напр. сгрешена сума"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={onClose}>
              Откажи
            </Button>
            <Button
              variant="destructive"
              className="flex-1"
              disabled={voidTx.isPending}
              onClick={() => void handleVoid()}
            >
              {voidTx.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Сторнирай
            </Button>
          </div>
        </div>
      ) : null}
    </ResponsiveDialog>
  );
}
